import React, { useState } from "react";
import { Popover } from "react-tiny-popover";
import { useSnapshot } from "valtio";
import { Button } from "@shared/components/Button";
import { PopoverContent } from "@shared/components/PopoverContent";
import { cn } from "@shared/components/utils";
import { WardObject } from "@shared/Map/types";
import { toolState } from "@shared/states/uiStates";
import { PencilOptions } from "@shared/Toolbar/PencilButton/PencilPopup";
import { ShapeOptions } from "@shared/Toolbar/ShapePopup/ShapePopup";
import { TextOptions } from "@shared/Toolbar/TextButton/TextPopup";
import { WardOptions } from "@shared/Toolbar/WardButton/WardSelectorPopup";
import { MapTool } from "@shared/types/tools";
import { MobileToolIcon } from "./MobileToolIcon";

const MOBILE_TOOLS = [
	MapTool.PAN,
	MapTool.MOVE,
	MapTool.PENCIL,
	MapTool.TEXT,
	MapTool.SHAPE,
	MapTool.WARD,
	MapTool.ERASER,
];

const TOOLS_WITH_OPTIONS = [MapTool.PENCIL, MapTool.TEXT, MapTool.SHAPE, MapTool.WARD];

const ToolOptions: React.FC<{ tool: MapTool }> = ({ tool }) => {
	const toolSnap = useSnapshot(toolState);

	if (tool === MapTool.PENCIL) return <PencilOptions />;
	if (tool === MapTool.TEXT) return <TextOptions />;
	if (tool === MapTool.SHAPE) return <ShapeOptions />;
	if (tool === MapTool.WARD) {
		const handleWard = (color: string, area: string) => {
			toolState.properties[MapTool.WARD].color = color as WardObject["color"];
			toolState.properties[MapTool.WARD].areaColor = area as WardObject["areaColor"];
		};
		return (
			<WardOptions
				currentColor={toolSnap.properties[MapTool.WARD].color}
				currentArea={toolSnap.properties[MapTool.WARD].areaColor}
				onSelect={handleWard}
			/>
		);
	}
	return null;
};

const MobileToolButton: React.FC<{ tool: MapTool }> = ({ tool }) => {
	const toolSnap = useSnapshot(toolState);
	const [isOpen, setIsOpen] = useState(false);
	const selected = toolSnap.selectedTool === tool;
	const hasOptions = TOOLS_WITH_OPTIONS.includes(tool);

	const handleClick = () => {
		if (!selected) {
			toolState.selectedTool = tool;
			setIsOpen(false);
			return;
		}
		if (hasOptions) setIsOpen((v) => !v);
	};

	return (
		<Popover
			isOpen={isOpen}
			positions={["bottom", "left", "right"]}
			padding={6}
			content={
				<PopoverContent className={"w-fit max-w-[92vw]"}>
					<ToolOptions tool={tool} />
				</PopoverContent>
			}
			onClickOutside={() => setIsOpen(false)}
		>
			<div className={"shrink-0"}>
				<Button
					data-testid={`mobile-tool-${MapTool[tool]}`}
					variant={"ghost"}
					size={"icon-sm"}
					onClick={handleClick}
					className={cn("text-white", selected && "bg-content-primary")}
				>
					<MobileToolIcon tool={tool} size={24} />
				</Button>
			</div>
		</Popover>
	);
};

export const MobileToolStrip = () => {
	return (
		<div className={"flex items-center gap-1 overflow-x-auto flex-1 min-w-0"}>
			{MOBILE_TOOLS.map((tool) => (
				<MobileToolButton key={tool} tool={tool} />
			))}
		</div>
	);
};
